import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import TitlePage from 'components/atoms/title-page'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { ALERT_TYPES } from 'constants/alert-constants'
import { ITEM_STATUS } from 'domains/internal-request/constants'
import InternalRequestDetail from 'domains/internal-request/organisms/internal-request-detail'
import InternalRequestItemTable from 'domains/internal-request/organisms/item-table'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useCallback } from 'react'
import { useHistory, useParams } from 'react-router'
import {
  Alert, Button, Card,
  CardBody, CardHeader, CardTitle
} from 'reactstrap'


const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const InternalRequestRequestorNewReceivePage = ({ pageUtils }) => {
  const [ internalRequest, setInternalRequest ]                     = useState({})
  const [ nonRejectedItems, setNonRejectedItems ]                   = useState([])
  const [ rejectedItems, setRejectedItems ]                         = useState([])
  const [ paginationNonRejectedItem, setPaginationNonRejectedItem ] = useState({})
  const [ paginationRejectedItem, setPaginationRejectedItem ]       = useState({})
  const [ isReceiveModal, setIsReceiveModal ]                       = useState(false)
  const [ isReceiving, setIsReceiving ] = useState(false)
  const history                                                     = useHistory()
  const { id }                                                      = useParams()

  // Hide particular columns
  const hiddenColumns = [
    'status',
    'borrowed',
    'quantity_put_away',
    'marked_for_return',
    'returned',
    'action'
  ]

  const receiveModalToggle = () => {
    if (!isReceiving) setIsReceiveModal(!isReceiveModal)
  }

  /** Fetching only non rejected Internal Request Item */
  const handleFetchNonRejectedItems = useCallback((tableState) => {

    const nonRejectedStatuses = Object.values(ITEM_STATUS).filter((status) => status !== ITEM_STATUS.REJECTED)

    const additionalFilters = {
      status: nonRejectedStatuses
    }

    OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      tableState          : tableState,
      additionalFilters   : additionalFilters
    })
      .then((response) => {
        setNonRejectedItems(response.data.internal_request_items)
        setPaginationNonRejectedItem(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])


  /** Fetching only rejected Internal Request Item */
  const handleFetchRejectedItems = useCallback((tableState) => {

    const additionalFilters = {
      status: [
        ITEM_STATUS.REJECTED
      ]
    }

    OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      tableState          : tableState,
      additionalFilters   : additionalFilters
    })
      .then((response) => {
        const { data: { internal_request_items } } = response

        setRejectedItems(internal_request_items.filter((item) => item.status === ITEM_STATUS.REJECTED))
        setPaginationRejectedItem(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])

  /** Handler to receive Item */
  const handleReceiveItemsOutboundInternalRequest = async () => {

    if (isReceiving || internalRequest.can_receive !== true) return
    setIsReceiving(true)
    try {
      await OutboundInternalRequestsApiV2.receiveAll(id)
      pageUtils.setAlertMsg('Internal Request items Successfully Received', ALERT_TYPES.SUCCESS)
      window.dispatchEvent(new Event('internal-request-updated'))
      setIsReceiveModal(false)
      history.goBack()
    } catch (error) {
      pageUtils.setApiErrorMsg(error.response?.data || { message: 'Receive gagal. Refresh dan periksa status request.' })
    } finally {
      setIsReceiving(false)
    }
  }

  useEffect(() => {
    OutboundInternalRequestsApiV2.show(id)
      .then((response) => setInternalRequest(response.data.internal_request))
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ id, pageUtils ])

  useEffect(() => {
    handleFetchNonRejectedItems()
    handleFetchRejectedItems()
  }, [ handleFetchNonRejectedItems, handleFetchRejectedItems ])

  return (
    <>
      <TitlePage mainTitle='Internal Request' subTitle='New Receive page' />

      <InternalRequestDetail internal_request={ internalRequest } />
      { internalRequest.receive_on_behalf && <Alert color='warning'>Anda akan menerima barang atas nama requestor { internalRequest.request_by || '-' }. Pastikan barang sudah diterima. Sistem tetap menyimpan requestor asli dan mencatat akun Anda sebagai penerima.</Alert> }
      { internalRequest.id && !internalRequest.can_receive && <Alert color='info'>Receive hanya untuk request Dispatched milik Anda. Owner/admin dapat mengambil alih penerimaan. Refresh jika request sudah diproses.</Alert> }

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{ 'Borrowed Items' }</h2>
          </CardTitle>
          <Button
            color='info'
            onClick={ () => { history.goBack()} }
          >
            { 'Back' }
          </Button>
          <Button
            className='float-right'
            color='success'
            disabled={ isReceiving || internalRequest.can_receive !== true }
            onClick={ () => receiveModalToggle() }
          >
            { isReceiving ? 'Memproses...' : 'Receive All' }
          </Button>
        </CardHeader>
        <CardBody>
          <InternalRequestItemTable
            data={ nonRejectedItems }
            pagination={ paginationNonRejectedItem }
            onFetchData={ handleFetchNonRejectedItems }
            hiddenColumns={ [
              ...hiddenColumns,
              'reject_reason'
            ] }
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2>{ 'Rejected Items' }</h2>
        </CardHeader>
        <CardBody>
          <InternalRequestItemTable
            data={ rejectedItems }
            pagination={ paginationRejectedItem }
            onFetchData={ handleFetchRejectedItems }
            hiddenColumns={ hiddenColumns }
          />
        </CardBody>
      </Card>

      <ConfirmationModal
        title={ 'Receive All Items of Internal Request' }
        isOpen={ isReceiveModal }
        toggle={ receiveModalToggle }
        handleConfirmation= { () => handleReceiveItemsOutboundInternalRequest() }
      >
        { internalRequest.receive_on_behalf ? `Terima seluruh item yang dikirim atas nama ${internalRequest.request_by || 'requestor'}? Pastikan barang sudah diterima.` : 'Apakah seluruh barang yang dikirim sudah Anda terima?' }
      </ConfirmationModal>
    </>
  )
}

InternalRequestRequestorNewReceivePage.propTypes = propTypes

export default InternalRequestRequestorNewReceivePage
